import React, { KeyboardEvent } from 'react';
import { TextArea, IconButton, useStyles2 } from '@grafana/ui';
import { css, cx, keyframes } from '@emotion/css';
import { GrafanaTheme2 } from '@grafana/data';
import { useInlineAssistant } from '../../inline/useInlineAssistant';
import { createBorderAnimation, getBackgroundGradientColors } from '../../utils/theme';
import { AIInputProps } from './types';

export interface AITextAreaProps extends AIInputProps {
  /** Number of rows for the textarea */
  rows?: number;
}

/**
 * An AI-powered controlled textarea component that uses the Grafana Assistant engine to generate content.
 * Features an integrated send button as well as a gradient border animation while generating.
 */
export function AITextArea({
  value,
  onChange,
  placeholder,
  origin,
  systemPrompt,
  onComplete,
  onError,
  onDelta,
  disabled = false,
  rows = 4,
  className,
  'data-testid': dataTestId,
}: AITextAreaProps) {
  const { generate, isGenerating, error, reset } = useInlineAssistant();

  // Determine if send button should be disabled
  const isSendDisabled = !value.trim() || disabled || isGenerating;
  const styles = useStyles2(getStyles);

  const handleGenerate = async () => {
    if (isSendDisabled) {
      return;
    }

    const prompt = value.trim();
    reset(); // Clear previous content/error

    await generate({
      prompt,
      origin,
      systemPrompt,
      onComplete: (text) => {
        onComplete?.(text);
      },
      onError: (err) => {
        onError?.(err);
      },
      onDelta: (delta) => {
        onDelta?.(delta);
      },
    });
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Cmd/Ctrl + Enter to send
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleGenerate();
    }
  };

  return (
    <div
      className={cx(styles.textareaWrapper, { [styles.animatedWrapper]: isGenerating }, className)}
      data-testid={dataTestId}
    >
      <TextArea
        value={value}
        onKeyDown={handleKeyDown}
        onChange={(e) => onChange(e.currentTarget.value)}
        placeholder={placeholder ?? 'Ask AI to generate... (Cmd/Ctrl+Enter to send)'}
        disabled={disabled || isGenerating}
        rows={rows}
        className={styles.textarea}
        invalid={!!error}
        data-testid={dataTestId ? `${dataTestId}-textarea` : undefined}
      />
      <IconButton
        name="ai-sparkle"
        onClick={handleGenerate}
        disabled={isSendDisabled}
        className={styles.sendButton}
        aria-label="Generate with AI"
        tooltip={isSendDisabled ? 'Enter text to generate' : 'Generate with AI (Cmd/Ctrl+Enter)'}
        data-testid={dataTestId ? `${dataTestId}-send` : undefined}
      />
    </div>
  );
}

const getStyles = (theme: GrafanaTheme2) => {
  const backgroundColor = theme.colors.background.primary;
  const borderAnimation = keyframes({
    label: 'ai-textarea-border-animation',
    ...createBorderAnimation(theme, backgroundColor),
  });

  const [gradientColor1, gradientColor2] = getBackgroundGradientColors(theme);

  return {
    textareaWrapper: css({
      label: 'ai-textarea-wrapper',
      position: 'relative',
      borderRadius: theme.shape.radius.default,
    }),
    animatedWrapper: css({
      label: 'ai-textarea-animated',
      border: `1px solid transparent`,
      // Set initial backgroundImage with both gradients so Chrome can animate properly
      backgroundImage: `
        linear-gradient(${backgroundColor}, ${backgroundColor}),
        conic-gradient(from 0deg, transparent 60%, ${gradientColor1} 80%, ${gradientColor2} 100%, transparent 15%)
      `,
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box',
      animation: `${borderAnimation} 2s linear infinite`,
      '& textarea': {
        // Remove the default border from the textarea when animated wrapper is active
        border: 'none !important',
      },
    }),
    textarea: css({
      label: 'ai-textarea',
      // ensures the text does not "underflow" the send button
      paddingRight: theme.spacing(4),
      // Hide scrollbar
      scrollbarWidth: 'none', // Firefox
      '&::-webkit-scrollbar': {
        display: 'none', // Chrome, Safari, Edge
      },
    }),
    sendButton: css({
      label: 'ai-textarea-send',
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
    }),
  };
};
